import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../integrations/supabase/types";
import { command, validateWrite } from "./commands";
import {
  activeSession,
  assertSession,
  clone,
  deactivateDemoSession,
  fail,
  failure,
  localUser,
  now,
  requireRole,
  result,
  subscribe,
  table,
  transaction,
  uid,
  type DemoResult,
  type DemoSession,
  type Row,
} from "./state";
export {
  initializeDemoSession,
  getDemoPersonas,
  getDemoPersonaId,
  setDemoPersona,
  resetDemoSession,
} from "./state";

type Filter = (row: Row) => boolean;
type Operation = "select" | "insert" | "upsert" | "update" | "delete";
const CONTRIBUTORS = [
  "admin",
  "quality_director",
  "quality_engineer",
  "manufacturing_engineer",
  "supplier_quality",
];

/** A finite adapter for the exported screens. There is no SDK instance or transport. */
class DemoQuery implements PromiseLike<DemoResult> {
  private session: DemoSession | null = null;
  private epoch = 0;
  private invalid: unknown = null;
  private operation: Operation = "select";
  private columns = "*";
  private filters: Filter[] = [];
  private sorts: { field: string; ascending: boolean; nullsFirst: boolean }[] = [];
  private maximum: number | null = null;
  private shape: "many" | "single" | "maybeSingle" = "many";
  private values: Row[] = [];
  private conflict = "id";
  private returnRows = false;
  private pending: Promise<DemoResult> | null = null;
  constructor(private name: string) {
    try {
      this.session = activeSession();
      this.epoch = this.session.epoch;
      table(this.session, name);
    } catch (error) {
      this.invalid = error;
    }
    return new Proxy(this, {
      get: (target, property, receiver) => {
        if (typeof property === "symbol" || property in target) {
          const value = Reflect.get(target, property, receiver);
          return typeof value === "function"
            ? (...args: unknown[]) => {
                const returned = value.apply(target, args);
                return returned === target ? receiver : returned;
              }
            : value;
        }
        return () => {
          target.invalid = new Error(`Unsupported demo query method: ${String(property)}`);
          return receiver;
        };
      },
    });
  }
  private column(name: string) {
    if (!/^[a-z][a-z0-9_]*$/i.test(name))
      this.invalid = new Error(`Unsupported demo column expression: ${name}`);
    return name;
  }
  select(columns = "*", options?: Row) {
    if (options && Object.keys(options).some((key) => !["count", "head"].includes(key)))
      this.invalid = new Error("Unsupported demo select options.");
    if (options?.head) this.invalid = new Error("Head-only demo queries are unsupported.");
    this.columns = columns;
    if (columns !== "*" && columns.split(",").some((c) => !/^[a-z][a-z0-9_]*$/i.test(c.trim())))
      this.invalid = new Error(`Unsupported demo projection: ${columns}`);
    if (this.operation !== "select") this.returnRows = true;
    return this;
  }
  eq(field: string, value: unknown) {
    this.column(field);
    if (field === "tenant_id" && this.session && value !== this.session.stored.tenantId)
      this.invalid = new Error("A demo query cannot select another tenant's data.");
    if (field === "membership_id" && this.session && value !== this.session.stored.membershipId)
      this.invalid = new Error("A demo query cannot select another membership's data.");
    this.filters.push(
      (row) =>
        (field === "tenant_id"
          ? this.session?.stored.tenantId
          : field === "membership_id"
            ? this.session?.stored.membershipId
            : row[field]) === value,
    );
    return this;
  }
  in(field: string, values: unknown[]) {
    this.column(field);
    if (!Array.isArray(values)) this.invalid = new Error("A demo in-filter requires an array.");
    if (
      field === "tenant_id" &&
      this.session &&
      values.some((v) => v !== this.session!.stored.tenantId)
    )
      this.invalid = new Error("Cross-tenant demo selection is refused.");
    if (
      field === "membership_id" &&
      this.session &&
      values.some((v) => v !== this.session!.stored.membershipId)
    )
      this.invalid = new Error("Cross-membership demo selection is refused.");
    this.filters.push((row) =>
      values.includes(
        field === "tenant_id"
          ? this.session?.stored.tenantId
          : field === "membership_id"
            ? this.session?.stored.membershipId
            : row[field],
      ),
    );
    return this;
  }
  or(expression: string) {
    const parts = expression.split(",").map((part) => /^([a-z][a-z0-9_]*)\.eq\.(.+)$/i.exec(part));
    if (parts.some((part) => !part || ["tenant_id", "membership_id"].includes(part[1])))
      this.invalid = new Error(
        "Unsupported demo or-filter; only ordinary column equality is supported.",
      );
    this.filters.push((row) => parts.some((part) => part && String(row[part[1]]) === part[2]));
    return this;
  }
  order(field: string, options: { ascending?: boolean; nullsFirst?: boolean } = {}) {
    this.sorts.push({
      field: this.column(field),
      ascending: options.ascending !== false,
      nullsFirst: options.nullsFirst === true,
    });
    return this;
  }
  limit(maximum: number) {
    if (!Number.isInteger(maximum) || maximum < 0)
      this.invalid = new Error("Invalid demo query limit.");
    this.maximum = maximum;
    return this;
  }
  single() {
    this.shape = "single";
    return this;
  }
  maybeSingle() {
    this.shape = "maybeSingle";
    return this;
  }
  insert(values: Row | Row[]) {
    this.operation = "insert";
    this.values = clone(Array.isArray(values) ? values : [values]);
    return this;
  }
  upsert(values: Row | Row[], options: { onConflict?: string; ignoreDuplicates?: boolean } = {}) {
    this.operation = "upsert";
    this.values = clone(Array.isArray(values) ? values : [values]);
    this.conflict = options.onConflict ?? "id";
    if (
      options.ignoreDuplicates ||
      this.conflict.split(",").some((name) => !/^[a-z][a-z0-9_]*$/i.test(name))
    )
      this.invalid = new Error("Unsupported demo upsert options.");
    return this;
  }
  update(values: Row) {
    this.operation = "update";
    this.values = [clone(values)];
    return this;
  }
  delete() {
    this.operation = "delete";
    return this;
  }
  private project(rows: Row[]) {
    const selected =
      this.columns === "*"
        ? clone(rows)
        : rows.map((row) =>
            Object.fromEntries(
              this.columns
                .split(",")
                .map((field) => [field.trim(), clone(row[field.trim()] ?? null)]),
            ),
          );
    if (this.shape === "single" && selected.length !== 1)
      fail(`Expected one demo row, found ${selected.length}.`);
    if (this.shape === "maybeSingle" && selected.length > 1)
      fail(`Expected at most one demo row, found ${selected.length}.`);
    return this.shape === "many" ? selected : (selected[0] ?? null);
  }
  private execute(): DemoResult {
    try {
      if (this.invalid) throw this.invalid;
      const s = this.session ?? fail("No active demo session.");
      assertSession(s, this.epoch);
      let rows = table(s, this.name).filter((row) => this.filters.every((f) => f(row)));
      const compare = (a: Row, b: Row) => {
        for (const order of this.sorts) {
          const av = a[order.field],
            bv = b[order.field];
          const n =
            av == null && bv == null
              ? 0
              : av == null
                ? order.nullsFirst
                  ? -1
                  : 1
                : bv == null
                  ? order.nullsFirst
                    ? 1
                    : -1
                  : av < bv
                    ? -1
                    : av > bv
                      ? 1
                      : 0;
          if (n) return order.ascending ? n : -n;
        }
        return 0;
      };
      if (this.sorts.length) rows.sort(compare);
      if (this.maximum !== null) rows = rows.slice(0, this.maximum);
      if (this.operation === "select") return { ...result(this.project(rows)), count: rows.length };
      requireRole(s, CONTRIBUTORS);
      const data = transaction(s, () => {
        const target = table(s, this.name);
        const changed: Row[] = [];
        if (["update", "delete"].includes(this.operation) && !this.filters.length)
          fail("A filtered demo mutation is required.");
        if (this.operation === "update") {
          for (const previous of rows) {
            const next = validateWrite(
              s,
              this.name,
              "update",
              { ...previous, ...this.values[0] },
              previous,
            );
            Object.assign(previous, next);
            changed.push(previous);
          }
        } else if (this.operation === "delete") {
          for (const previous of rows) validateWrite(s, this.name, "delete", previous, previous);
          s.stored.tables[this.name] = target.filter((r) => !rows.includes(r));
          changed.push(...rows);
        } else {
          for (const value of this.values) {
            if (!value || typeof value !== "object") fail("A demo row object is required.");
            const keys = this.conflict.split(",");
            const previous = keys.every((key) => value[key] != null)
              ? target.find((r) => keys.every((key) => r[key] === value[key]))
              : undefined;
            if (this.operation === "insert" && value.id && target.some((r) => r.id === value.id))
              fail("A demo row with this id already exists.");
            const candidate = validateWrite(
              s,
              this.name,
              this.operation,
              previous
                ? { ...previous, ...value }
                : { id: uid(), created_at: now(), updated_at: now(), ...value },
              previous,
            );
            const next = { ...candidate, simulation: true, classification: "SYNTHETIC" };
            if (previous) Object.assign(previous, next);
            else target.push(next);
            changed.push(previous ?? next);
          }
        }
        return this.returnRows ? this.project(changed) : null;
      });
      return result(data);
    } catch (error) {
      return failure(error);
    }
  }
  then<TResult1 = DemoResult, TResult2 = never>(
    onfulfilled?: ((value: DemoResult) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    this.pending ??= Promise.resolve().then(() => this.execute());
    return this.pending.then(onfulfilled, onrejected);
  }
}

function unsupported(name: string) {
  return async () => failure(new Error(`Unsupported demo operation: ${name}`));
}
function finite<T extends object>(target: T, prefix: string): T {
  return new Proxy(target, {
    get: (object, key) =>
      key in object ? Reflect.get(object, key) : unsupported(`${prefix}.${String(key)}`),
  });
}
const auth = finite(
  {
    async getUser() {
      try {
        return { data: { user: localUser() }, error: null };
      } catch (error) {
        return { data: { user: null }, error: failure(error).error };
      }
    },
    async getSession() {
      try {
        return { data: { session: { user: localUser(), simulation: true } }, error: null };
      } catch (error) {
        return { data: { session: null }, error: failure(error).error };
      }
    },
    onAuthStateChange: subscribe,
    // Closing this simulation never touches the real portal authentication/session.
    async signOut() {
      deactivateDemoSession();
      return { error: null };
    },
  },
  "auth",
);

function validPath(s: DemoSession, bucket: string, path: string) {
  if (bucket !== "record-evidence") fail(`Unsupported demo evidence bucket: ${bucket}`);
  const bits = path.split("/");
  if (
    bits.length !== 3 ||
    bits.some(
      (bit) =>
        !bit ||
        bit === "." ||
        bit === ".." ||
        bit.includes("\\") ||
        bit.includes(String.fromCharCode(0)),
    )
  )
    fail("Unsupported demo evidence path.");
  const record =
    table(s, "problem_records").find((r) => r.id === bits[0]) ??
    fail("Evidence record was not found in the active demo.");
  const body = record.record as Row;
  const gates = [
    ...(body.stages ?? []),
    ...(body.eightD ?? []),
    ...(body.clauses ?? []).map((clause: Row) => ({ id: `clause-${clause.id}` })),
  ];
  if (!gates.some((gate) => gate.id === bits[1]))
    fail("Evidence gate was not found in the active demo.");
}
const storage = finite(
  {
    from(bucket: string) {
      let s: DemoSession | null = null;
      let epoch = 0;
      let initializationError: unknown;
      try {
        s = activeSession();
        epoch = s.epoch;
      } catch (error) {
        initializationError = error;
      }
      const checked = (path: string) => {
        if (initializationError) throw initializationError;
        const session = s ?? fail("No active demo session.");
        assertSession(session, epoch);
        validPath(session, bucket, path);
        return session;
      };
      return finite(
        {
          async upload(path: string, file: Blob, options: { upsert?: boolean } = {}) {
            try {
              const session = checked(path);
              requireRole(session, CONTRIBUTORS);
              if (!(file instanceof Blob) || file.size > 2 * 1024 * 1024)
                fail(
                  "Local demo evidence accepts files up to 2 MB. Use a reference for larger evidence.",
                );
              if (session.stored.files[path] && !options.upsert)
                fail("Local demo file already exists.");
              const bytes = new Uint8Array(await file.arrayBuffer());
              assertSession(session, epoch);
              let binary = "";
              for (const b of bytes) binary += String.fromCharCode(b);
              const data = btoa(binary);
              transaction(session, () => {
                session.stored.files[path] = {
                  data,
                  type: file.type || "application/octet-stream",
                  name: "name" in file ? String(file.name) : path.split("/").pop()!,
                };
              });
              return result({ path, id: path, fullPath: `${bucket}/${path}` });
            } catch (error) {
              return failure(error);
            }
          },
          async createSignedUrl(path: string, _expires: number) {
            try {
              const session = checked(path);
              const file =
                session.stored.files[path] ??
                fail("This exported evidence is reference-only; no local file is available.");
              const binary = atob(file.data);
              const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
              // Uploaded HTML/SVG must never run with the portal's origin.
              const signedUrl = URL.createObjectURL(
                new Blob([bytes], { type: "application/octet-stream" }),
              );
              // Blob URLs are local browser references; no server signs or fetches a URL.
              setTimeout(() => URL.revokeObjectURL(signedUrl), 300_000);
              return result({ signedUrl });
            } catch (error) {
              return failure(error);
            }
          },
          async remove(paths: string[]) {
            try {
              if (!paths.length) fail("Choose a local demo evidence path.");
              const session = checked(paths[0]);
              requireRole(session, CONTRIBUTORS);
              for (const p of paths) {
                checked(p);
                if (!session.stored.files[p]) fail("Local demo file not found.");
              }
              return result(
                transaction(session, () =>
                  paths.map((path) => {
                    delete session.stored.files[path];
                    return { name: path };
                  }),
                ),
              );
            } catch (error) {
              return failure(error);
            }
          },
        },
        "storage.bucket",
      );
    },
  },
  "storage",
);
const adapter = finite(
  {
    from(name: string) {
      return new DemoQuery(name);
    },
    async rpc(fn: string, args: Row = {}) {
      try {
        const s = activeSession();
        return result(transaction(s, () => clone(command(s, fn, clone(args)) ?? null)));
      } catch (error) {
        return failure(error);
      }
    },
    auth,
    storage,
    functions: finite({ invoke: unsupported("functions.invoke") }, "functions"),
  },
  "client",
);

// Preserve the source's generated row/function inference without importing an SDK value.
export const supabase = adapter as unknown as SupabaseClient<Database>;
